import { create } from 'zustand';
import { leadSourcesApi, leadSubmissionsApi } from '../lib/api';
import type {
  LeadDiscardReason,
  LeadQueueFilters,
  LeadSource,
  LeadSubmissionListItem,
  LeadSubmissionStatus,
} from '../types/lead';

interface LeadState {
  submissions: LeadSubmissionListItem[];
  sources: LeadSource[];
  pendingCount: number;
  statusFilter: LeadSubmissionStatus;
  filters: LeadQueueFilters;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetchQueue: (status?: LeadSubmissionStatus) => Promise<void>;
  fetchCount: () => Promise<void>;
  fetchSources: (includeInactive?: boolean) => Promise<void>;
  setFilters: (patch: Partial<LeadQueueFilters>) => Promise<void>;
  clearFilters: () => Promise<void>;
  approve: (id: string, opts?: { assignToUserId?: string | null; sourceId?: string | null }) => Promise<void>;
  discard: (id: string, reason: LeadDiscardReason) => Promise<void>;
  createSource: (label: string) => Promise<void>;
  updateSource: (id: string, body: { label?: string; isActive?: boolean }) => Promise<void>;
}

/**
 * Guards fetchQueue against out-of-order resolution: bumped on every call, so an
 * older in-flight request that resolves after a newer one (e.g. a slow "Approved"
 * fetch landing after a fast tab-switch back to "Pending") can tell it is stale
 * and must not clobber the newer result.
 */
let fetchGeneration = 0;

export const useLeadStore = create<LeadState>((set, get) => ({
  submissions: [],
  sources: [],
  pendingCount: 0,
  statusFilter: 'PENDING_REVIEW',
  filters: {},
  isLoading: false,
  isSaving: false,
  error: null,

  fetchQueue: async (status) => {
    const nextStatus = status ?? get().statusFilter;
    set({ isLoading: true, error: null, statusFilter: nextStatus });

    // assignedTo reflects the resolved person's CURRENT assignee, which only makes
    // sense on the Approved tab — drop it from stored filter state on any other
    // tab so it never silently survives and re-applies on a later return to
    // Approved. Once dropped here, the request below needs no tab special-case.
    if (nextStatus !== 'APPROVED' && get().filters.assignedTo) {
      const { assignedTo, ...rest } = get().filters;
      set({ filters: rest });
    }

    const gen = ++fetchGeneration;
    try {
      const { leadSubmissions } = await leadSubmissionsApi.list(nextStatus, get().filters);
      if (gen !== fetchGeneration) return; // a newer fetchQueue call already landed
      set({ submissions: leadSubmissions, isLoading: false });
    } catch (e: any) {
      if (gen !== fetchGeneration) return;
      set({ isLoading: false, error: e.response?.data?.message || 'Failed to load leads' });
    }
  },

  fetchCount: async () => {
    try {
      const { count } = await leadSubmissionsApi.count();
      set({ pendingCount: count });
    } catch { /* badge is best-effort */ }
  },

  fetchSources: async (includeInactive = true) => {
    try {
      const { leadSources } = await leadSourcesApi.list(includeInactive);
      set({ sources: leadSources });
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Failed to load sources' });
    }
  },

  setFilters: async (patch: Partial<LeadQueueFilters>) => {
    const merged = { ...get().filters, ...patch };
    (Object.keys(merged) as (keyof LeadQueueFilters)[]).forEach((k) => {
      if (!merged[k]) delete merged[k];
    });
    set({ filters: merged });
    await get().fetchQueue();
  },

  clearFilters: async () => {
    set({ filters: {} });
    await get().fetchQueue();
  },

  approve: async (id, opts = {}) => {
    set({ isSaving: true });
    try {
      await leadSubmissionsApi.approve(id, opts);
      await Promise.all([get().fetchQueue(), get().fetchCount()]);
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to approve lead');
    } finally {
      set({ isSaving: false });
    }
  },

  discard: async (id, reason) => {
    set({ isSaving: true });
    try {
      await leadSubmissionsApi.discard(id, reason);
      await Promise.all([get().fetchQueue(), get().fetchCount()]);
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to discard lead');
    } finally {
      set({ isSaving: false });
    }
  },

  createSource: async (label) => {
    set({ isSaving: true });
    try {
      await leadSourcesApi.create(label);
      await get().fetchSources();
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to create source');
    } finally {
      set({ isSaving: false });
    }
  },

  updateSource: async (id, body) => {
    set({ isSaving: true });
    try {
      await leadSourcesApi.update(id, body);
      await get().fetchSources();
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to update source');
    } finally {
      set({ isSaving: false });
    }
  },
}));
